import React, { useCallback, useEffect, useMemo, useState } from "react";
import MuiDialog from "@material-ui/core/Dialog";
import styled from "styled-components";
import {
  Button,
  Card,
  EmptyState,
  SectionHeader,
  Skeleton,
  Tag,
} from "../../../design-system";
import {
  ExperimentAgent,
  ExperimentEvidenceSide,
  ExperimentTask,
  NotificationExperimentCommandAction,
  controlNotificationExperiment,
  getExperimentDashboard,
} from "../../../database/analytics/experimentDashboardApi";
import { IconChart, IconSparkles } from "../../../design-system/icons/icons";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const ControlRoom = styled.section`
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(220px, 0.8fr) minmax(0, 1.4fr);
  gap: var(--space-8);
  padding: var(--space-7);
  background:
    radial-gradient(
      circle at 88% 15%,
      var(--color-accent-subtle),
      transparent 42%
    ),
    var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: var(--space-5);
    padding: var(--space-5);
  }
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-accent-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
`;

const HeroNumber = styled.div`
  margin-top: var(--space-3);
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: clamp(3.5rem, 8vw, 5.5rem);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.07em;
  line-height: 0.9;
`;

const HeroLabel = styled.p`
  max-width: 30ch;
  margin: var(--space-3) 0 0;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-content: end;
  border-left: 1px solid var(--color-border);

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    border-left: 0;
    border-top: 1px solid var(--color-border);
  }
`;

const SummaryItem = styled.div`
  padding: var(--space-3) var(--space-5);
  border-right: 1px solid var(--color-border-subtle);

  &:last-child {
    border-right: 0;
  }

  @media (max-width: 600px) {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3) 0;
    border-right: 0;
    border-bottom: 1px solid var(--color-border-subtle);

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const SummaryValue = styled.div`
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const SummaryLabel = styled.div`
  margin-top: var(--space-1);
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(250px, 0.8fr);
  gap: var(--space-5);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const ExperimentCard = styled(Card)`
  position: relative;
  overflow: hidden;

  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    background: var(--color-accent);
    content: "";
  }
`;

const TaskHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
`;

const Monogram = styled.div`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.02em;
`;

const AgentName = styled.div`
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const AgentRole = styled.div`
  margin-top: 1px;
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
`;

const Hypothesis = styled.h3`
  max-width: 62ch;
  margin: var(--space-4) 0 var(--space-3);
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  letter-spacing: -0.01em;
  line-height: var(--line-height-snug);
  text-wrap: pretty;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-4);
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
`;

const TaskActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-top: var(--space-4);
`;

const SuccessCard = styled(Card)`
  color: var(--color-success);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
`;

const EvidenceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
  margin-top: var(--space-5);

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const EvidenceCell = styled.div`
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface-subtle);
  border-radius: var(--radius-md);
`;

const EvidenceLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
`;

const EvidenceValue = styled.div`
  margin-top: var(--space-2);
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const AgentList = styled.div`
  display: flex;
  flex-direction: column;
`;

const AgentRow = styled.button<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  width: 100%;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-3) 0;
  background: ${(p) =>
    p.$selected ? "var(--color-accent-subtle)" : "transparent"};
  color: inherit;
  border: 0;
  border-bottom: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    transform var(--duration-fast) var(--ease-standard);

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: var(--color-surface-subtle);
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const OpenAgent = styled.span`
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-lg);
  line-height: 1;
`;

const ActivityDialog = styled(MuiDialog)`
  .MuiDialog-paper {
    width: min(760px, calc(100vw - 32px));
    max-width: 760px;
    max-height: min(820px, calc(100dvh - 32px));
    margin: var(--space-4);
    overflow: hidden;
    background: var(--color-surface);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-xl);
  }
`;

const ModalShell = styled.article`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ModalHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-5);
  padding: var(--space-6);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const ModalIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.03em;
  line-height: var(--line-height-tight);
`;

const ModalSubtitle = styled.p`
  margin: var(--space-1) 0 0;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
`;

const ModalSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  background: var(--color-border-subtle);
  border-bottom: 1px solid var(--color-border-subtle);

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ModalStat = styled.div`
  min-width: 0;
  padding: var(--space-4) var(--space-5);
  background: var(--color-surface-subtle);

  strong {
    display: block;
    overflow: hidden;
    color: var(--color-text);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 2px;
    color: var(--color-text-tertiary);
    font-family: var(--font-sans);
    font-size: var(--font-size-xs);
  }
`;

const ActivityBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-5) var(--space-6) var(--space-7);
`;

const ActivityHeading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-3);

  h3 {
    margin: 0;
    color: var(--color-text);
    font-family: var(--font-sans);
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
  }

  span {
    color: var(--color-text-tertiary);
    font-family: var(--font-sans);
    font-size: var(--font-size-xs);
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActivityItem = styled.article`
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr);
  gap: var(--space-3);
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: 0;
  }
`;

const Timeline = styled.div`
  position: relative;
  display: flex;
  justify-content: center;

  &::before {
    position: absolute;
    top: 12px;
    bottom: calc(var(--space-4) * -1);
    width: 1px;
    background: var(--color-border);
    content: "";
  }

  ${ActivityItem}:last-child &::before {
    display: none;
  }
`;

const TimelineDot = styled.span<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  display: block;
  width: 8px;
  height: 8px;
  margin-top: 5px;
  background: ${(p) =>
    p.$active ? "var(--color-accent)" : "var(--color-border-strong)"};
  border: 2px solid var(--color-surface);
  border-radius: var(--radius-full);
  box-shadow: 0 0 0 1px
    ${(p) => (p.$active ? "var(--color-accent)" : "var(--color-border)")};
`;

const ActivityTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
`;

const ActivityTitle = styled.h4`
  max-width: 58ch;
  margin: 0;
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-snug);
  text-wrap: pretty;
`;

const ActivityMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  margin-top: var(--space-2);
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
`;

const ActivityReason = styled.p`
  margin: var(--space-2) 0 0;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
`;

const QueueRow = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 0.45fr) minmax(0, 1.4fr) auto;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr auto;

    > :nth-child(2) {
      grid-column: 1 / -1;
      grid-row: 2;
    }
  }
`;

const QueueTitle = styled.div`
  overflow: hidden;
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QueueReason = styled.div`
  margin-top: 2px;
  overflow: hidden;
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ErrorCard = styled(Card)`
  color: var(--color-danger);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
`;

const LoadingCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const statusTone = (
  status: string
): "success" | "accent" | "warning" | "danger" | "neutral" | "info" => {
  if (status === "running") return "success";
  if (status === "measuring") return "accent";
  if (status === "ready_for_review" || status === "awaiting_pricing_approval")
    return "warning";
  if (status === "blocked" || status === "error") return "danger";
  if (status === "completed") return "info";
  return "neutral";
};

const humanize = (value?: string | null) =>
  value
    ? value
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Not set";

const formatDate = (value?: string | null) => {
  if (!value) return "Not run yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not run yet";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatEvidence = (
  side: ExperimentEvidenceSide,
  metric: string | null
) => {
  if (side.value == null) return "—";
  if (metric === "revenue_per_eligible_paywall_visitor") {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(side.value);
  }
  if (metric === "views") return Math.round(side.value).toLocaleString();
  return `${(side.value * 100).toFixed(1)}%`;
};

const taskTitle = (task: ExperimentTask) =>
  task.hypothesis ||
  [task.category, task.mutation].filter(Boolean).map(humanize).join(" · ") ||
  `${task.agentRole} experiment`;

const activeClaimLabel = (task: ExperimentTask): string | null => {
  if (!task.claimedBy || !task.claimExpiresAt) return null;
  const expiresAt = Date.parse(task.claimExpiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return null;
  return `Claimed by ${task.claimedBy} until ${formatDate(
    task.claimExpiresAt
  )}`;
};

const demoTask = (
  input: Partial<ExperimentTask> &
    Pick<
      ExperimentTask,
      "id" | "agentId" | "agentName" | "agentRole" | "status"
    >
): ExperimentTask => ({
  cycle: "2026-07",
  category: null,
  cohortKey: null,
  platform: null,
  experimentId: null,
  hypothesis: null,
  primaryMetric: null,
  mutation: null,
  measurementWindowDays: null,
  startedAt: null,
  updatedAt: new Date().toISOString(),
  completedAt: null,
  verdict: null,
  reason: null,
  claimedBy: null,
  claimExpiresAt: null,
  blockedReasons: [],
  evidence: {
    metric: null,
    control: { value: null, sampleSize: null },
    candidate: { value: null, sampleSize: null },
    ageDays: null,
  },
  ...input,
});

const DEMO_AGENTS: ExperimentAgent[] = [
  {
    id: "milo",
    name: "MILO",
    role: "Welcome email",
    configured: true,
    enabled: true,
    autoStart: true,
    lastRunAt: new Date().toISOString(),
    lastQueueSyncAt: new Date().toISOString(),
    attention: null,
    tasks: [
      demoTask({
        id: "demo-milo",
        agentId: "milo",
        agentName: "MILO",
        agentRole: "Welcome email",
        status: "measuring",
        category: "welcome",
        experimentId: "welcome_activation_v3",
        hypothesis:
          "A shorter first email with one clear next step will increase first-action activation.",
        primaryMetric: "activation_first_action",
        mutation: "email_body",
        measurementWindowDays: 14,
        startedAt: new Date(Date.now() - 6 * 86_400_000).toISOString(),
        verdict: "continue",
        reason: "Waiting for enough activation events.",
        evidence: {
          metric: "activation_first_action",
          control: { value: 0.237, sampleSize: 118 },
          candidate: { value: 0.292, sampleSize: 113 },
          ageDays: 6,
        },
      }),
      demoTask({
        id: "demo-milo-previous",
        agentId: "milo",
        agentName: "MILO",
        agentRole: "Welcome email",
        status: "completed",
        cycle: "2026-06",
        category: "welcome",
        experimentId: "welcome_plan_start_v2",
        hypothesis:
          "Putting the first plan recommendation in the subject line will increase plan starts.",
        primaryMetric: "plan_started",
        mutation: "subject",
        startedAt: new Date(Date.now() - 36 * 86_400_000).toISOString(),
        updatedAt: new Date(Date.now() - 21 * 86_400_000).toISOString(),
        completedAt: new Date(Date.now() - 21 * 86_400_000).toISOString(),
        verdict: "candidate_wins",
        reason: "Candidate produced a reliable lift and became the new control.",
        evidence: {
          metric: "plan_started",
          control: { value: 0.184, sampleSize: 207 },
          candidate: { value: 0.241, sampleSize: 212 },
          ageDays: 15,
        },
      }),
    ],
  },
  {
    id: "funnel",
    name: "Funnel",
    role: "Onboarding → Paywall",
    configured: true,
    enabled: true,
    autoStart: false,
    lastRunAt: new Date(Date.now() - 3_600_000).toISOString(),
    lastQueueSyncAt: new Date().toISOString(),
    attention: null,
    tasks: [
      demoTask({
        id: "demo-mara",
        agentId: "funnel",
        agentName: "Funnel",
        agentRole: "Onboarding → Paywall",
        status: "running",
        category: "paywall",
        platform: "ios",
        experimentId: "paywall_social_proof_v2",
        hypothesis:
          "Moving member proof beside the annual plan will increase revenue per eligible visitor.",
        primaryMetric: "revenue_per_eligible_paywall_visitor",
        mutation: "blocks",
        measurementWindowDays: 45,
        startedAt: new Date(Date.now() - 12 * 86_400_000).toISOString(),
        verdict: "continue",
        reason: "Evaluation window is still open.",
        evidence: {
          metric: "revenue_per_eligible_paywall_visitor",
          control: { value: 2.84, sampleSize: 304 },
          candidate: { value: 3.19, sampleSize: 297 },
          ageDays: 12,
        },
      }),
      demoTask({
        id: "demo-mara-previous",
        agentId: "funnel",
        agentName: "Funnel",
        agentRole: "Onboarding → Paywall",
        status: "rolled_back",
        cycle: "2026-05",
        category: "paywall",
        platform: "ios",
        experimentId: "paywall_default_annual_v1",
        hypothesis:
          "Preselecting annual billing will increase revenue without harming conversion.",
        primaryMetric: "revenue_per_eligible_paywall_visitor",
        mutation: "default_plan",
        startedAt: new Date(Date.now() - 63 * 86_400_000).toISOString(),
        updatedAt: new Date(Date.now() - 56 * 86_400_000).toISOString(),
        completedAt: new Date(Date.now() - 56 * 86_400_000).toISOString(),
        verdict: "guardrail_stop",
        reason:
          "Candidate increased declines beyond the configured safety threshold.",
        evidence: {
          metric: "revenue_per_eligible_paywall_visitor",
          control: { value: 2.67, sampleSize: 181 },
          candidate: { value: 2.12, sampleSize: 176 },
          ageDays: 7,
        },
      }),
    ],
  },
  {
    id: "nudge",
    name: "NUDGE",
    role: "Member notifications",
    configured: true,
    enabled: true,
    autoStart: false,
    lastRunAt: new Date(Date.now() - 7_200_000).toISOString(),
    lastQueueSyncAt: new Date().toISOString(),
    attention: { pendingCount: 1, blockedCount: 0, attentionRequired: true },
    tasks: [
      demoTask({
        id: "demo-nudge",
        agentId: "nudge",
        agentName: "NUDGE",
        agentRole: "Member notifications",
        status: "drafted",
        category: "streak_rescue",
        experimentId: "streak_rescue_timing_v1",
        hypothesis:
          "Sending the streak rescue two hours earlier will increase habit check-ins.",
        primaryMetric: "habit_checkin",
        mutation: "timing",
        reason: "Ready for production-start approval.",
      }),
      demoTask({
        id: "demo-nudge-previous",
        agentId: "nudge",
        agentName: "NUDGE",
        agentRole: "Member notifications",
        status: "completed",
        cycle: "2026-06",
        category: "plan_continuation",
        experimentId: "plan_continuation_copy_v1",
        hypothesis:
          "Naming the unfinished lesson will increase plan continuation.",
        primaryMetric: "plan_continued",
        mutation: "body",
        startedAt: new Date(Date.now() - 42 * 86_400_000).toISOString(),
        updatedAt: new Date(Date.now() - 24 * 86_400_000).toISOString(),
        completedAt: new Date(Date.now() - 24 * 86_400_000).toISOString(),
        verdict: "candidate_wins",
        reason: "The candidate improved the intended action with reliable evidence.",
        evidence: {
          metric: "plan_continued",
          control: { value: 0.311, sampleSize: 148 },
          candidate: { value: 0.385, sampleSize: 143 },
          ageDays: 18,
        },
      }),
    ],
  },
  {
    id: "solo",
    name: "SOLO",
    role: "Social content",
    configured: true,
    enabled: true,
    autoStart: true,
    lastRunAt: new Date(Date.now() - 14_400_000).toISOString(),
    lastQueueSyncAt: new Date().toISOString(),
    attention: { pendingCount: 1, blockedCount: 1, attentionRequired: true },
    tasks: [
      demoTask({
        id: "demo-solo",
        agentId: "solo",
        agentName: "SOLO",
        agentRole: "Social content",
        status: "blocked",
        cycle: "2026-W30",
        platform: "instagram",
        hypothesis:
          "A direct problem-led hook will improve saves on educational carousels.",
        primaryMetric: "save_rate",
        mutation: "hook",
        reason: "Waiting for enough measured baseline posts.",
        blockedReasons: ["insufficient_measured_concepts"],
      }),
    ],
  },
];

const TaskEvidence: React.FC<{ task: ExperimentTask }> = ({ task }) => {
  const { evidence } = task;
  const hasEvidence =
    evidence.control.value != null || evidence.candidate.value != null;
  if (!hasEvidence) {
    return (
      <MetaRow>
        <span>Evidence will appear after the first evaluation run.</span>
      </MetaRow>
    );
  }
  return (
    <EvidenceGrid>
      {(
        [
          ["Control", evidence.control],
          ["Candidate", evidence.candidate],
        ] as const
      ).map(([label, side]) => (
        <EvidenceCell key={label}>
          <EvidenceLabel>
            <span>{label}</span>
            <span>
              {side.sampleSize == null
                ? "Sample pending"
                : `n=${side.sampleSize.toLocaleString()}`}
            </span>
          </EvidenceLabel>
          <EvidenceValue>
            {formatEvidence(side, evidence.metric || task.primaryMetric)}
          </EvidenceValue>
        </EvidenceCell>
      ))}
    </EvidenceGrid>
  );
};

const ExperimentsTab: React.FC<{ communityId?: string }> = ({
  communityId,
}) => {
  const demoMode =
    process.env.NODE_ENV === "development" &&
    new URLSearchParams(window.location.search).get("experimentsDemo") === "1";
  const [agents, setAgents] = useState<ExperimentAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [commandKey, setCommandKey] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] =
    useState<ExperimentAgent["id"] | null>(null);

  const load = useCallback(
    async (manual = false) => {
      if (demoMode) {
        setAgents(DEMO_AGENTS);
        setGeneratedAt(new Date().toISOString());
        setError(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!communityId) {
        setAgents([]);
        setLoading(false);
        return;
      }
      manual ? setRefreshing(true) : setLoading(true);
      setError(null);
      try {
        const dashboard = await getExperimentDashboard(communityId);
        setAgents(dashboard.agents);
        setGeneratedAt(dashboard.generatedAt);
      } catch (err: any) {
        setError(err?.message || "Experiment data could not be loaded.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [communityId, demoMode]
  );

  useEffect(() => {
    load();
  }, [load]);

  const runNotificationCommand = useCallback(
    async (
      task: ExperimentTask,
      action: NotificationExperimentCommandAction
    ) => {
      if (!communityId || task.agentId !== "nudge") return;
      let reason: string | undefined;
      if (action === "start") {
        const confirmed = window.confirm(
          "Start this member-facing notification experiment? NUDGE must also have production starts enabled."
        );
        if (!confirmed) return;
      } else if (action === "evaluate") {
        const confirmed = window.confirm(
          "Evaluate now and apply the evidence-based decision? A winner may be promoted and a guardrail breach may roll back."
        );
        if (!confirmed) return;
      } else {
        const entered = window.prompt(
          "Why are you rolling back this notification experiment?"
        );
        if (!entered) return;
        reason = entered.trim();
        if (reason.length < 8) {
          setError("Please provide a rollback reason of at least 8 characters.");
          return;
        }
      }
      const key = `${task.id}:${action}`;
      setCommandKey(key);
      setError(null);
      setNotice(null);
      try {
        const command = await controlNotificationExperiment({
          communityId,
          taskId: task.id,
          action,
          reason,
        });
        setNotice(
          `${humanize(action)} queued as command ${command.commandId}. NUDGE will validate and process it through the durable adapter.`
        );
        await load(true);
      } catch (err: any) {
        setError(
          err?.message || "The notification experiment command failed."
        );
      } finally {
        setCommandKey(null);
      }
    },
    [communityId, load]
  );

  const tasks = useMemo(
    () => agents.flatMap((agent) => agent.tasks),
    [agents]
  );
  const live = tasks.filter((task) =>
    ["running", "measuring", "ready_for_review"].includes(task.status)
  );
  const attention = tasks.filter((task) =>
    [
      "blocked",
      "error",
      "ready_for_review",
      "awaiting_pricing_approval",
    ].includes(task.status)
  );
  const queued = tasks.filter((task) =>
    ["queued", "drafted", "approved", "awaiting_pricing_approval"].includes(
      task.status
    )
  );
  const recent = tasks
    .filter((task) =>
      ["completed", "rolled_back", "inconclusive"].includes(task.status)
    )
    .slice(0, 6);
  const enabledAgents = agents.filter((agent) => agent.enabled).length;
  const selectedAgent = agents.find(
    (agent) => agent.id === selectedAgentId
  );
  const selectedAgentActive =
    selectedAgent?.tasks.filter((task) =>
      ["running", "measuring", "ready_for_review"].includes(task.status)
    ).length || 0;
  const selectedAgentCompleted =
    selectedAgent?.tasks.filter((task) =>
      ["completed", "rolled_back", "inconclusive"].includes(task.status)
    ).length || 0;

  if (loading && agents.length === 0) {
    return (
      <Stack>
        <LoadingCard padding="lg">
          <Skeleton width="28%" height={16} />
          <Skeleton width="58%" height={54} />
          <Skeleton width="100%" height={120} variant="rect" />
        </LoadingCard>
        <LoadingCard padding="lg">
          <Skeleton width="35%" height={18} />
          <Skeleton width="100%" height={180} variant="rect" />
        </LoadingCard>
      </Stack>
    );
  }

  return (
    <Stack>
      <ControlRoom>
        <div>
          <Eyebrow>
            <IconSparkles size={14} />
            Experiment control room
            {demoMode && (
              <Tag variant="accent" size="sm">
                Preview data
              </Tag>
            )}
          </Eyebrow>
          <HeroNumber>{live.length}</HeroNumber>
          <HeroLabel>
            {live.length === 1
              ? "experiment is gathering evidence right now."
              : "experiments are gathering evidence right now."}
          </HeroLabel>
        </div>
        <SummaryGrid>
          <SummaryItem>
            <SummaryValue>{enabledAgents}</SummaryValue>
            <SummaryLabel>agents on duty</SummaryLabel>
          </SummaryItem>
          <SummaryItem>
            <SummaryValue>{queued.length}</SummaryValue>
            <SummaryLabel>up next</SummaryLabel>
          </SummaryItem>
          <SummaryItem>
            <SummaryValue>{attention.length}</SummaryValue>
            <SummaryLabel>need attention</SummaryLabel>
          </SummaryItem>
        </SummaryGrid>
      </ControlRoom>

      {error && (
        <ErrorCard padding="md">
          {error} Existing data remains visible. Try refreshing in a moment.
        </ErrorCard>
      )}
      {notice && <SuccessCard padding="md">{notice}</SuccessCard>}

      <ContentGrid>
        <Card padding="lg">
          <SectionHeader
            title="Live experiments"
            description="Control versus candidate, judged on the signal that actually matters."
            actions={
              <Button
                variant="secondary"
                size="sm"
                isLoading={refreshing}
                onClick={() => load(true)}
              >
                Refresh
              </Button>
            }
          />
          {live.length === 0 ? (
            <EmptyState
              icon={<IconChart size={20} />}
              title="No experiments are live"
              description="Queued work will move here when an agent starts a controlled test."
            />
          ) : (
            <TaskList>
              {live.map((task) => (
                <ExperimentCard key={`${task.agentId}:${task.id}`} padding="lg">
                  <TaskHeader>
                    <Identity>
                      <Monogram>{task.agentName.slice(0, 2)}</Monogram>
                      <div>
                        <AgentName>{task.agentName}</AgentName>
                        <AgentRole>{task.agentRole}</AgentRole>
                      </div>
                    </Identity>
                    <Tag variant={statusTone(task.status)} size="sm" withDot>
                      {humanize(task.status)}
                    </Tag>
                  </TaskHeader>
                  <Hypothesis>{taskTitle(task)}</Hypothesis>
                  <MetaRow>
                    <span>
                      Signal: {humanize(task.primaryMetric)}
                    </span>
                    {task.mutation && (
                      <span>Changed: {humanize(task.mutation)}</span>
                    )}
                    {task.category && (
                      <span>Category: {humanize(task.category)}</span>
                    )}
                    {task.cohortKey && (
                      <span>Cohort: {humanize(task.cohortKey)}</span>
                    )}
                    {task.evidence.ageDays != null && (
                      <span>{task.evidence.ageDays.toFixed(0)} days live</span>
                    )}
                    {task.measurementWindowDays != null && (
                      <span>
                        Compared at day {task.measurementWindowDays}
                      </span>
                    )}
                  </MetaRow>
                  <TaskEvidence task={task} />
                  {task.agentId === "nudge" && (
                    <TaskActions>
                      <Button
                        variant="secondary"
                        size="sm"
                        isLoading={
                          commandKey === `${task.id}:evaluate`
                        }
                        onClick={() =>
                          runNotificationCommand(task, "evaluate")
                        }
                      >
                        Evaluate now
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        isLoading={
                          commandKey === `${task.id}:rollback`
                        }
                        onClick={() =>
                          runNotificationCommand(task, "rollback")
                        }
                      >
                        Roll back
                      </Button>
                    </TaskActions>
                  )}
                </ExperimentCard>
              ))}
            </TaskList>
          )}
        </Card>

        <Card padding="lg">
          <SectionHeader
            title="Agent desk"
            description={`Updated ${formatDate(generatedAt)}`}
          />
          <AgentList>
            {agents.map((agent) => {
              const current = agent.tasks.find((task) =>
                ["running", "measuring", "ready_for_review"].includes(
                  task.status
                )
              );
              return (
                <AgentRow
                  key={agent.id}
                  type="button"
                  $selected={selectedAgentId === agent.id}
                  aria-haspopup="dialog"
                  aria-expanded={selectedAgentId === agent.id}
                  aria-label={`Open ${agent.name} activity, ${
                    agent.tasks.length
                  } task${agent.tasks.length === 1 ? "" : "s"}`}
                  onClick={() => setSelectedAgentId(agent.id)}
                >
                  <Monogram>{agent.name.slice(0, 2)}</Monogram>
                  <div>
                    <AgentName>{agent.name}</AgentName>
                    <AgentRole>
                      {current
                        ? humanize(current.status)
                        : `${agent.tasks.length} task${
                            agent.tasks.length === 1 ? "" : "s"
                          }`}
                    </AgentRole>
                  </div>
                  <Tag
                    variant={
                      agent.enabled && agent.configured ? "success" : "neutral"
                    }
                    size="sm"
                    withDot={agent.enabled && agent.configured}
                  >
                    {!agent.configured
                      ? "Not initialized"
                      : agent.enabled
                      ? "On"
                      : "Off"}
                  </Tag>
                  <OpenAgent aria-hidden="true">›</OpenAgent>
                </AgentRow>
              );
            })}
          </AgentList>
        </Card>
      </ContentGrid>

      {(queued.length > 0 || attention.length > 0) && (
        <Card padding="lg">
          <SectionHeader
            title="Work queue"
            description="Drafts, approvals, and blockers before the next test can run."
            actions={
              <Tag
                variant={attention.length ? "warning" : "neutral"}
                size="sm"
              >
                {attention.length
                  ? `${attention.length} need attention`
                  : `${queued.length} queued`}
              </Tag>
            }
          />
          {[...attention, ...queued]
            .filter(
              (task, index, all) =>
                all.findIndex(
                  (candidate) =>
                    candidate.id === task.id &&
                    candidate.agentId === task.agentId
                ) === index
            )
            .slice(0, 12)
            .map((task) => (
              <QueueRow key={`${task.agentId}:${task.id}`}>
                <Identity>
                  <Monogram>{task.agentName.slice(0, 2)}</Monogram>
                  <div>
                    <AgentName>{task.agentName}</AgentName>
                    <AgentRole>
                      {humanize(task.category || task.platform)}
                    </AgentRole>
                  </div>
                </Identity>
                <div>
                  <QueueTitle>{taskTitle(task)}</QueueTitle>
                  <QueueReason>
                    {task.blockedReasons.length
                      ? `Waiting on ${task.blockedReasons
                          .map(humanize)
                          .join(", ")}`
                      : activeClaimLabel(task) ||
                        task.reason ||
                        `Updated ${formatDate(task.updatedAt)}`}
                  </QueueReason>
                </div>
                {task.agentId === "nudge" &&
                task.status === "drafted" ? (
                  <Button
                    variant="primary"
                    size="sm"
                    isLoading={commandKey === `${task.id}:start`}
                    onClick={() =>
                      runNotificationCommand(task, "start")
                    }
                  >
                    Start
                  </Button>
                ) : (
                  <Tag variant={statusTone(task.status)} size="sm">
                    {humanize(task.status)}
                  </Tag>
                )}
              </QueueRow>
            ))}
        </Card>
      )}

      {recent.length > 0 && (
        <Card padding="lg">
          <SectionHeader
            title="Recently decided"
            description="The latest completed, rolled-back, or inconclusive tests."
          />
          {recent.map((task) => (
            <QueueRow key={`${task.agentId}:${task.id}`}>
              <AgentName>{task.agentName}</AgentName>
              <div>
                <QueueTitle>{taskTitle(task)}</QueueTitle>
                <QueueReason>
                  {task.reason || `Finished ${formatDate(task.completedAt)}`}
                </QueueReason>
              </div>
              <Tag variant={statusTone(task.status)} size="sm">
                {humanize(task.verdict || task.status)}
              </Tag>
            </QueueRow>
          ))}
        </Card>
      )}

      <ActivityDialog
        open={Boolean(selectedAgent)}
        onClose={() => setSelectedAgentId(null)}
        aria-labelledby="agent-activity-title"
      >
        {selectedAgent && (
          <ModalShell>
            <ModalHeader>
              <ModalIdentity>
                <Monogram>{selectedAgent.name.slice(0, 2)}</Monogram>
                <div>
                  <ModalTitle id="agent-activity-title">
                    {selectedAgent.name} activity
                  </ModalTitle>
                  <ModalSubtitle>
                    {selectedAgent.role} · recent work and decisions
                  </ModalSubtitle>
                </div>
              </ModalIdentity>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedAgentId(null)}
              >
                Close
              </Button>
            </ModalHeader>

            <ModalSummary>
              <ModalStat>
                <strong>{selectedAgent.tasks.length}</strong>
                <span>tasks retained</span>
              </ModalStat>
              <ModalStat>
                <strong>{selectedAgentActive}</strong>
                <span>active now</span>
              </ModalStat>
              <ModalStat>
                <strong>{selectedAgentCompleted}</strong>
                <span>decided</span>
              </ModalStat>
              <ModalStat>
                <strong>{formatDate(selectedAgent.lastRunAt)}</strong>
                <span>last agent run</span>
              </ModalStat>
            </ModalSummary>

            <ActivityBody>
              <ActivityHeading>
                <h3>Task backlog</h3>
                <span>Newest first</span>
              </ActivityHeading>
              {selectedAgent.tasks.length === 0 ? (
                <EmptyState
                  icon={<IconChart size={20} />}
                  title="No tasks recorded"
                  description="This agent has not created work for this community yet."
                />
              ) : (
                <ActivityList>
                  {selectedAgent.tasks.map((task) => {
                    const isActive = [
                      "running",
                      "measuring",
                      "ready_for_review",
                    ].includes(task.status);
                    return (
                      <ActivityItem key={`${task.agentId}:${task.id}`}>
                        <Timeline>
                          <TimelineDot $active={isActive} />
                        </Timeline>
                        <div>
                          <ActivityTop>
                            <ActivityTitle>{taskTitle(task)}</ActivityTitle>
                            <Tag
                              variant={statusTone(task.status)}
                              size="sm"
                              withDot={isActive}
                            >
                              {humanize(task.status)}
                            </Tag>
                          </ActivityTop>
                          <ActivityMeta>
                            <span>
                              {formatDate(
                                task.completedAt || task.updatedAt
                              )}
                            </span>
                            {task.primaryMetric && (
                              <span>
                                Signal: {humanize(task.primaryMetric)}
                              </span>
                            )}
                            {task.mutation && (
                              <span>
                                Changed: {humanize(task.mutation)}
                              </span>
                            )}
                            {task.verdict && (
                              <span>
                                Decision: {humanize(task.verdict)}
                              </span>
                            )}
                          </ActivityMeta>
                          {(task.reason || task.blockedReasons.length > 0) && (
                            <ActivityReason>
                              {task.blockedReasons.length > 0
                                ? `Waiting on ${task.blockedReasons
                                    .map(humanize)
                                    .join(", ")}.`
                                : task.reason}
                            </ActivityReason>
                          )}
                          <TaskEvidence task={task} />
                        </div>
                      </ActivityItem>
                    );
                  })}
                </ActivityList>
              )}
            </ActivityBody>
          </ModalShell>
        )}
      </ActivityDialog>
    </Stack>
  );
};

export default ExperimentsTab;
